import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { mockData } from '../mock';

const normalSteps = [
  { n: 1, title: 'デザイン作成', thumb: '/images/step1.png' },
  { n: 2, title: 'プロトタイプ製造', thumb: '/images/step2.png' },
  { n: 3, title: 'プロトタイプ完成', thumb: '/images/step3.png' },
  { n: 4, title: 'スタジオ撮影', thumb: '/images/step4.png' },
  { n: 5, title: 'サムネイル完成', thumb: '/images/step5.png' },
];

// 課題データ
const problems = [
  {
    title: '納品までの期間が長く、手間が多い',
    description: 'デザイン作成からプロトタイプ製造、撮影まで1〜2ヶ月かかり、修正のたびに時間とコストが発生します。',
    image: '/images/problem1_long_delivery.png'
  },
  {
    title: '商品の良さが上手く伝わらない',
    description: '平面的な写真では質感や立体感が伝わりにくく、顧客の購買意欲を引き出せません。',
    image: '/images/problem2_poor_communication.png'
  },
  {
    title: 'A/Bテストが難しい・コストがかかる',
    description: '複数パターンの撮影には追加の時間と費用が必要で、素早い改善サイクルが回せません。',
    image: '/images/problem3_ab_testing_cost.png'
  }
];

const ProblemsSection = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const itemRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.dataset.index);
            setVisibleItems((prev) => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.2 }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* タイトル */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-6xl font-black font-din tracking-tight">
            <span className="text-gray-700 font-extrabold">サムネイル画像</span><span className="text-3xl lg:text-5xl text-gray-600">を作るとき、</span><br />
            <span className="text-3xl lg:text-5xl text-gray-600">こんな</span><span className="text-gray-700 font-extrabold">課題</span><span className="text-3xl lg:text-5xl text-gray-600">に直面していませんか？</span>
          </h2>
        </div>

        {/* 課題セクション - 左右交互スライドイン */}
        <div className="space-y-16 sm:space-y-20 mb-20">
          {problems.map((problem, index) => {
            const isLeft = index % 2 === 0;
            const isVisible = visibleItems.includes(index);
            const numberText = `0${index + 1}`;

            return (
              <div
                key={index}
                ref={(el) => (itemRefs.current[index] = el)}
                data-index={index}
                className={`flex flex-col-reverse ${isLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-12 transition-all duration-1000 ${
                  isVisible
                    ? 'opacity-100 translate-x-0'
                    : `opacity-0 ${isLeft ? '-translate-x-20' : 'translate-x-20'}`
                }`}
              >
                {/* 画像側 */}
                <div className="w-full lg:w-1/2 mb-8 lg:mb-0">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300">
                    <img
                      src={problem.image}
                      alt={problem.title}
                      className="w-full h-[300px] sm:h-[400px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  </div>
                </div>

                {/* テキスト側 */}
                <div className="w-full lg:w-1/2">
                  <div 
                    className="relative bg-gradient-to-br from-gray-50 to-gray-100 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-visible"
                    style={{
                      borderTopLeftRadius: isLeft ? '1.5rem' : '100px',
                      borderTopRightRadius: isLeft ? '100px' : '1.5rem',
                      borderBottomLeftRadius: '1.5rem',
                      borderBottomRightRadius: '1.5rem'
                    }}
                  >
                    {/* ナンバリング - 円周上に配置 */}
                    <div 
                      className="absolute"
                      style={{
                        zIndex: 10,
                        top: '29px',
                        left: isLeft ? 'auto' : '29px',
                        right: isLeft ? '0' : 'auto',
                        transform: isLeft ? 'translate(0, -50%)' : 'translate(-50%, -50%)'
                      }}
                    >
                      <span className="text-6xl sm:text-7xl lg:text-8xl font-black font-din tracking-tighter leading-none text-transparent bg-gradient-to-br from-gray-500 via-gray-600 to-gray-700 bg-clip-text drop-shadow-lg">
                        {numberText}
                      </span>
                    </div>

                    <div className="p-6 sm:p-8 pt-16 sm:pt-20">
                      {/* タイトル */}
                      <h3 className="text-2xl sm:text-3xl font-black text-gray-700 mb-3 font-din tracking-tight leading-tight">
                        {problem.title}
                      </h3>

                      {/* 説明 */}
                      <p className="text-gray-700 leading-relaxed text-base sm:text-lg font-body">
                        {problem.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProblemsSection;
